import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const allCategories = categories;

  const [activeCategory, setActiveCategory] = useState(slug || '');
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  useScrollToTop();

  const currentCategory = activeCategory ? getCategoryBySlug(activeCategory) : undefined;
  const articles = activeCategory ? getArticlesByCategory(activeCategory) : [];

  const sortedArticles = useMemo(() => {
    if (sortBy === 'latest') {
      return [...articles].sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
    }
    return [...articles].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  }, [articles, sortBy]);

  const categorySchema = currentCategory
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${currentCategory.name} — 原神本子精选`,
        description: currentCategory.description,
        url: `https://artsygen.github.io/category/${currentCategory.slug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '原神本子精选',
          url: 'https://artsygen.github.io',
        },
      }
    : undefined;

  if (!category && slug) {
    return (
      <>
        <SEOHead title="分类未找到" description="您访问的分类页面不存在。" />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的分类页面不存在，请检查链接是否正确。</p>
            <Link to="/" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={currentCategory ? `${currentCategory.name}` : '分类浏览'}
        description={currentCategory?.description || '浏览原神同人创作的所有分类，包括角色同人本、同人漫画、插画集、小说本、CP同人本等。'}
        keywords={`原神本子,原神同人本,${currentCategory?.name || ''},原神同人漫画,原神角色同人`}
        canonicalUrl={`https://artsygen.github.io/category/${activeCategory}`}
        schema={categorySchema || undefined}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类浏览' },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {currentCategory?.name || '分类浏览'}
            </h1>
            {currentCategory && (
              <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
                {currentCategory.description}
              </p>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="w-full lg:w-56 flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="text-sm font-semibold text-foreground-700 mb-3">分类筛选</h3>
                <div className="space-y-1">
                  {allCategories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => setActiveCategory(cat.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                        activeCategory === cat.slug
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>

                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-3">排序方式</h3>
                  <div className="flex rounded-lg bg-background-100 p-1">
                    <button
                      onClick={() => setSortBy('latest')}
                      className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        sortBy === 'latest'
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      最新
                    </button>
                    <button
                      onClick={() => setSortBy('popular')}
                      className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        sortBy === 'popular'
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      精选
                    </button>
                  </div>
                </div>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              {sortedArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {sortedArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">该分类下暂无作品</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}